/**
 * White-Label Branding Service
 * Fetches and applies dynamic branding based on company slug/domain
 */

// Get API URL from environment or use /api as default
const API_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * White-label configuration from API
 */
export interface WhiteLabelConfig {
  id: number;
  company_id: number;
  app_name: string;
  company_name: string;
  tagline: string | null;
  subdomain: string | null;
  custom_domain: string | null;
  logo_url: string | null;
  favicon_url: string | null;
  login_background_url: string | null;
  primary_color: string;
  secondary_color: string | null;
  accent_color: string | null;
  support_email: string | null;
  support_url: string | null;
  terms_url: string | null;
  privacy_url: string | null;
  show_powered_by: boolean;
}

/**
 * Company info from API
 */
export interface CompanyInfo {
  id: number;
  name: string;
  slug: string;
  email: string;
  phone: string | null;
  subscription_tier: string;
  status: string;
  trial_ends_at: string | null;
  max_users: number;
  max_projects: number;
  created_at: string;
}

/**
 * Local storage key for cached branding
 */
const BRANDING_CACHE_KEY = 'tt_branding_config';
const COMPANY_SLUG_KEY = 'tt_company_slug';

/**
 * Rate limiting for branding requests
 */
const RATE_LIMIT_KEY = 'tt_branding_rate_limit';
const RATE_LIMIT_WINDOW = 60000; // 1 minute
const MAX_REQUESTS_PER_WINDOW = 5;

/**
 * Parse BASE_DOMAINS from environment variable or use defaults.
 * VITE_BASE_DOMAINS should be a comma-separated string, e.g.:
 *   "timetracker.shaemarcus.com,localhost,127.0.0.1"
 */
function parseBaseDomains(): string[] {
  const envValue = import.meta.env.VITE_BASE_DOMAINS;
  if (envValue && typeof envValue === 'string' && envValue.trim().length > 0) {
    return envValue.split(',').map(d => d.trim()).filter(Boolean);
  }
  // Default fallback — preserves backward compatibility
  return [
    'timetracker.shaemarcus.com',
    'localhost',
    '127.0.0.1',
  ];
}

/**
 * Known base domains - requests from these domains are NOT white-labeled.
 * Configurable via VITE_BASE_DOMAINS environment variable (comma-separated).
 * Falls back to hardcoded defaults if not set.
 */
const BASE_DOMAINS = parseBaseDomains();

/**
 * Extract company slug from subdomain
 * e.g., xyz-corp.timetracker.shaemarcus.com -> xyz-corp
 */
function getSlugFromSubdomain(): string | null {
  const hostname = window.location.hostname;
  
  // Skip if localhost or IP address
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return null;
  }
  
  // Check if this is a subdomain of any base domain
  for (const baseDomain of BASE_DOMAINS) {
    if (hostname === baseDomain) {
      // This is the base domain, no subdomain
      return null;
    }
    
    if (hostname.endsWith(`.${baseDomain}`)) {
      // Extract subdomain: xyz-corp.timetracker.shaemarcus.com -> xyz-corp
      const subdomain = hostname.slice(0, -(baseDomain.length + 1));
      
      // Ignore common subdomains like www, api, staging
      if (['www', 'api', 'staging', 'admin', 'app'].includes(subdomain)) {
        return null;
      }
      
      return subdomain;
    }
  }
  
  // Custom domain - look up by full hostname
  return `domain:${hostname}`;
}

/**
 * Get company slug from subdomain, URL parameter, or storage
 * Priority: subdomain > URL path > URL param (query string - legacy) > stored
 */
export function getCompanySlug(): string | null {
  const hostname = window.location.hostname;
  const isBaseDomain = BASE_DOMAINS.some(d => hostname === d || hostname.startsWith('localhost'));
  
  // First priority: subdomain detection
  const subdomainSlug = getSlugFromSubdomain();
  if (subdomainSlug) {
    // Store for future use within this session
    localStorage.setItem(COMPANY_SLUG_KEY, subdomainSlug);
    return subdomainSlug;
  }
  
  // Second priority: URL path-based company slug (e.g., /xyz-corp/login)
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const potentialSlug = pathParts[0];
  const knownRoutes = ['login', 'register', 'dashboard', 'projects', 'tasks', 'time', 
                       'teams', 'reports', 'settings', 'admin', 'staff', 'users',
                       'forgot-password', 'reset-password', 'request-account'];
  
  if (potentialSlug && !knownRoutes.includes(potentialSlug)) {
    // First path segment is a company slug (e.g., /xyz-corp/login)
    localStorage.setItem(COMPANY_SLUG_KEY, potentialSlug);
    return potentialSlug;
  }
  
  // Third priority: URL query parameters (legacy support - ?company=xyz)
  // Only use this for non-base domains or if explicitly requested
  const urlParams = new URLSearchParams(window.location.search);
  const slugFromUrl = urlParams.get('company');
  
  if (slugFromUrl && !isBaseDomain) {
    // Only store for non-base domains to prevent "sticking" issue
    localStorage.setItem(COMPANY_SLUG_KEY, slugFromUrl);
    return slugFromUrl;
  }
  
  // On base domain with no slug in path/subdomain - clear any stale stored slug
  if (isBaseDomain) {
    // Check if we're on a known route without company prefix - clear stale slug
    if (!potentialSlug || knownRoutes.includes(potentialSlug)) {
      localStorage.removeItem(COMPANY_SLUG_KEY);
    }
    return null;
  }
  
  // Fourth: Check stored slug (only for non-base domains)
  return localStorage.getItem(COMPANY_SLUG_KEY);
}

/**
 * Set company slug in storage
 */
export function setCompanySlug(slug: string): void {
  localStorage.setItem(COMPANY_SLUG_KEY, slug);
}

/**
 * Clear company slug
 */
export function clearCompanySlug(): void {
  localStorage.removeItem(COMPANY_SLUG_KEY);
  localStorage.removeItem(BRANDING_CACHE_KEY);
}

/**
 * Fetch branding configuration for a company
 * Supports both slug lookup and custom domain lookup
 */
export async function fetchBranding(slugOrDomain: string): Promise<WhiteLabelConfig | null> {
  try {
    // Rate limiting check
    const rateLimitData = localStorage.getItem(RATE_LIMIT_KEY);
    if (rateLimitData) {
      const { timestamp, count } = JSON.parse(rateLimitData);
      const now = Date.now();
      
      if (now - timestamp < RATE_LIMIT_WINDOW) {
        if (count >= MAX_REQUESTS_PER_WINDOW) {
          console.warn(`Rate limit exceeded for branding requests. Try again in ${Math.ceil((RATE_LIMIT_WINDOW - (now - timestamp)) / 1000)}s`);
          return getCachedBranding();
        }
        // Increment count
        localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify({ timestamp, count: count + 1 }));
      } else {
        // Reset window
        localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify({ timestamp: now, count: 1 }));
      }
    } else {
      // Initialize rate limit tracking
      localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify({ timestamp: Date.now(), count: 1 }));
    }

    let endpoint: string;
    
    // Check if this is a custom domain lookup (domain:hostname format)
    if (slugOrDomain.startsWith('domain:')) {
      const domain = slugOrDomain.slice(7); // Remove 'domain:' prefix
      endpoint = `${API_URL}/companies/branding/by-domain/${encodeURIComponent(domain)}`;
    } else {
      endpoint = `${API_URL}/companies/branding/${slugOrDomain}`;
    }
    
    const response = await fetch(endpoint);
    
    if (!response.ok) {
      console.warn(`Could not fetch branding for ${slugOrDomain}: ${response.status}`);
      return null;
    }
    
    const config: WhiteLabelConfig = await response.json();
    
    // Cache the config
    localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(config));
    
    return config;
  } catch (error) {
    console.error('Error fetching branding:', error);
    return null;
  }
}

/**
 * Get cached branding config
 */
export function getCachedBranding(): WhiteLabelConfig | null {
  try {
    const cached = localStorage.getItem(BRANDING_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

/**
 * Fetch company info by slug
 */
export async function fetchCompanyInfo(slug: string): Promise<CompanyInfo | null> {
  try {
    const response = await fetch(`${API_URL}/companies/by-slug/${slug}`);
    
    if (!response.ok) {
      console.warn(`Could not fetch company info for ${slug}: ${response.status}`);
      return null;
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching company info:', error);
    return null;
  }
}

/**
 * Generate color variants
 */
function adjustColorBrightness(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  
  return '#' + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1);
}

/**
 * B22: Validate a favicon URL before injecting it into a `<link href>`.
 * Accepts:
 *   - https:// URLs ending in a known image extension (with optional query string)
 *   - same-origin paths beginning with `/`
 * Rejects everything else (data:, javascript:, vbscript:, http://, etc.).
 * Exported for unit testing.
 */
export function isValidFaviconUrl(url: unknown): url is string {
  if (typeof url !== 'string' || url.length === 0) return false;
  if (url.startsWith('/')) return true;
  return /^https:\/\/[^\s]+\.(png|ico|svg|jpg|jpeg|webp)(\?.*)?$/i.test(url);
}

/**
 * B22: Validate a CSS color string. Accepts only `#RRGGBB` or `#RRGGBBAA`.
 * Exported for unit testing.
 */
export function isValidHexColor(color: unknown): color is string {
  if (typeof color !== 'string') return false;
  return /^#[0-9a-fA-F]{6}$/.test(color) || /^#[0-9a-fA-F]{8}$/.test(color);
}

/**
 * Apply branding to document
 */
export function applyBrandingToDocument(config: WhiteLabelConfig): void {
  const root = document.documentElement;

  // B22: Validate primary color before applying. Reject silently to default
  // palette (variable left untouched) with a console warning.
  if (isValidHexColor(config.primary_color)) {
    root.style.setProperty('--color-primary', config.primary_color);
    root.style.setProperty('--color-primary-hover', adjustColorBrightness(config.primary_color, -10));
    root.style.setProperty('--color-primary-light', adjustColorBrightness(config.primary_color, 40));
  } else {
    console.warn(
      `[branding] Rejected invalid primary_color: ${String(config.primary_color)}. Using default.`
    );
  }

  // B22: Apply secondary and accent colors only if they pass validation.
  if (config.secondary_color) {
    if (isValidHexColor(config.secondary_color)) {
      root.style.setProperty('--color-secondary', config.secondary_color);
      root.style.setProperty('--color-secondary-hover', adjustColorBrightness(config.secondary_color, -10));
    } else {
      console.warn(
        `[branding] Rejected invalid secondary_color: ${String(config.secondary_color)}. Using default.`
      );
    }
  }

  if (config.accent_color) {
    if (isValidHexColor(config.accent_color)) {
      root.style.setProperty('--color-accent', config.accent_color);
    } else {
      console.warn(
        `[branding] Rejected invalid accent_color: ${String(config.accent_color)}. Using default.`
      );
    }
  }

  // Update document title
  document.title = config.app_name;

  // B22: Update favicon ONLY when the URL passes validation. Reuse the
  // existing `<link rel="icon">` node if present — never appendChild a
  // duplicate. Branding failure must not break the page, so we log a
  // warning and keep the default favicon on rejection.
  if (config.favicon_url) {
    if (isValidFaviconUrl(config.favicon_url)) {
      let link = document.querySelector(
        'link[rel="icon"]'
      ) as HTMLLinkElement | null;
      if (!link) {
        // Fallback: some HTML uses `rel="shortcut icon"`.
        link = document.querySelector(
          "link[rel='shortcut icon']"
        ) as HTMLLinkElement | null;
      }
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.type = 'image/x-icon';
      link.href = config.favicon_url;
    } else {
      console.warn(
        `[branding] Rejected invalid favicon_url: ${String(config.favicon_url)}. Keeping default favicon.`
      );
    }
  }

  // Store for meta tag updates
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription && config.tagline) {
    metaDescription.setAttribute('content', config.tagline);
  }
}

/**
 * Initialize branding on app load
 */
export async function initializeBranding(): Promise<WhiteLabelConfig | null> {
  const slug = getCompanySlug();
  
  if (!slug) {
    return null;
  }
  
  // Try cached first
  let config = getCachedBranding();
  
  // Fetch fresh in background
  const freshConfig = await fetchBranding(slug);
  
  if (freshConfig) {
    config = freshConfig;
    applyBrandingToDocument(config);
  } else if (config) {
    // Use cached if fresh failed
    applyBrandingToDocument(config);
  }
  
  return config;
}

/**
 * Default branding for non-white-labeled access
 */
export const DEFAULT_BRANDING: WhiteLabelConfig = {
  id: 0,
  company_id: 0,
  app_name: 'Time Tracker',
  company_name: 'Time Tracker',
  tagline: 'Track time. Boost productivity.',
  subdomain: null,
  custom_domain: null,
  logo_url: '/logo.svg',
  favicon_url: '/favicon.svg',
  login_background_url: null,
  primary_color: '#2563eb',
  secondary_color: '#1d4ed8',
  accent_color: '#f97316',
  support_email: 'support@example.com',
  support_url: '/help',
  terms_url: '/terms',
  privacy_url: '/privacy',
  show_powered_by: true,
};
